import React, { useState, useEffect, useContext, ReactElement } from "react";
import { RouteComponentProps, Prompt } from "react-router-dom";
import FinancingRequestApiClient from "../../../api/FinancingRequestApiClient";
import FinancingRequestValidityRange from "../../../models/financing-request/validity-range/FinancingRequestValidityRange";
import Month from "../../../models/school-year/Month";
import StepperComponent from "../../shared/common/StepperComponent";
import ValidityRangePickerStep from "./range-picker/ValidityRangePickerStep";
import FinancingRequestValidityRangeStudentData from "../../../models/financing-request/validity-range/FinancingRequestValidityRangeStudentData";
import { nameof } from "../../../utilities/utilities";
import ValidityRangeReason from "../../../models/financing-request/validity-range/ValidityRangeReason";
import ValidityRangeReasonApiClient from "../../../api/ValidityRangeReasonApiClient";
import useCodeBook from "../../../hooks/codebook/useCodeBook";
import { financingRequestsUrl } from "../../../urls";
import { isSchoolWithdrawalMonth } from "../../../utilities/utilities";

interface MatchParams {
  id: string;
}

interface Props extends RouteComponentProps<MatchParams> { }

export default function FinancingRequestValidityRangeDataPage({
  match,
  history
}: Props) {
  const { id } = match.params;
  const apiClient = new FinancingRequestApiClient();

  const [initFinancingRequest, setInitFinancingRequest] = useState<
    FinancingRequestValidityRange
  >({
    studentsData: new Array<FinancingRequestValidityRangeStudentData>()
  } as FinancingRequestValidityRange);

  const [financingRequest, setFinancingRequest] = useState<
    FinancingRequestValidityRange
  >({
    studentsData: new Array<FinancingRequestValidityRangeStudentData>()
  } as FinancingRequestValidityRange);

  const [showWarningMessage, setShowWarningMessage] = React.useState<boolean>(
    true
  );

  const [hasChanged, setHasChanged] = React.useState<boolean>(false);

  useEffect(() => {
    async function getFinancingRequest() {
      const response = await apiClient.getFinancingRequestValidityRangeData(
        +id
      );
      setFinancingRequest(response);
      setInitFinancingRequest(response);
    }
    getFinancingRequest();
  }, []);

  const { validityRangeReasonSelectItems } = useCodeBook({
    getValidityRangeReason: true
  });

  const handleValidityRangeChange = (studentId: number) => (
    e: React.ChangeEvent<{ name?: string | undefined; value: unknown }>
  ) => {
    const { name, value } = e.target;
    if (name != null) {
      const newStudentsData = financingRequest.studentsData.map(studentData => {
        if (studentData.student.id == studentId) {
          if (isSchoolWithdrawalMonth(studentData)) {
            return { ...studentData, [name]: value, validityRangeReason: null };
          }
          return {
            ...studentData,
            [name]: value
          } as FinancingRequestValidityRangeStudentData;
        }
        if (
          name == nameof<typeof studentData>("validTo") &&
          studentData.correlatedPassengersInTransport.find(
            p => p == studentId
          ) != null
        ) {
          if (value != null) {
            return {
              ...studentData,
              validTo: value,
              validityRangeReason: ValidityRangeReason.PersonalTransportChange
            } as FinancingRequestValidityRangeStudentData;
          } else {
            return {
              ...studentData,
              validTo: value,
              validityRangeReason: null
            } as FinancingRequestValidityRangeStudentData;
          }
        }
        return studentData;
      });

      //check if something change from the inital values
      let changed = false;
      initFinancingRequest.studentsData.forEach(initStudenData => {
        var newStudentData = newStudentsData.find(
          nsd => nsd.student.id == initStudenData.student.id
        );
        if (
          newStudentData &&
          (newStudentData.validTo != initStudenData.validTo ||
            newStudentData.validityRangeReason !=
            initStudenData.validityRangeReason)
        ) {
          changed = true;
          return;
        }
      });
      setHasChanged(changed);

      setFinancingRequest(
        prevFin =>
        ({
          ...prevFin,
          studentsData: newStudentsData
        } as FinancingRequestValidityRange)
      );
    }
  };

  const studentHaveInitalValue = (studentId: number) => {
    if (
      initFinancingRequest &&
      initFinancingRequest.studentsData &&
      initFinancingRequest.studentsData.length > 0
    ) {
      const initalStudentData = initFinancingRequest.studentsData.find(
        sd => sd.student.id == studentId
      );
      if (initalStudentData && initalStudentData.validTo != null) {
        return true;
      }
    }
    return false;
  };

  const handleFinish = () => {
    async function updateFinancingRequestValidityRange() {
      const apiClient = new FinancingRequestApiClient();
      await apiClient.updateFinancingRequestValidityRange(financingRequest);

      setShowWarningMessage(false);
      history.push(financingRequestsUrl());
    }

    updateFinancingRequestValidityRange();
  };

  const getTitles = (): string[] => {
    return ["Odabir učenika", "Potvrda zahtjeva"];
  };

  const hasUnsavedChanges =
    showWarningMessage &&
    financingRequest.studentsData.some(s =>
      s.validTo != null || s.validityRangeReason != null
    );



  return (
    <>
      <StepperComponent
        steps={getTitles()}
        previousButtonText={"Prethodni korak"}
        nextButtonText={"Sljedeći korak"}
        lastStepButtonText={"Predaj zahtjev"}
        confirmationModalTitle={"Predaja zahtjeva"}
        confirmationModalMessage={
          "Potvrdom ove akcije spremate izmjene na godišnjem zahtjevu. Jeste li sigurni da želite predati zatjev?"
        }
        confirmationModalCancelBtnText="Odustani"
        confirmationModalOkBtnText="Predaj zahtjev"
        onFinish={handleFinish}
        isFinancingRequest={false}
      >
        <ValidityRangePickerStep
          financingRequest={financingRequest}
          selectChange={handleValidityRangeChange}
          validityRangeReasons={validityRangeReasonSelectItems}
          forEdit={true}
          hasChanged={hasChanged}
          studentDataAlreadySaved={studentHaveInitalValue}
        />
        <ValidityRangePickerStep
          financingRequest={financingRequest}
          selectChange={handleValidityRangeChange}
          validityRangeReasons={validityRangeReasonSelectItems}
          forEdit={false}
          hasChanged={hasChanged}
          studentDataAlreadySaved={studentHaveInitalValue}
        />
      </StepperComponent>
      <Prompt
        when={hasUnsavedChanges}
        message="Potvrdom ove akcije izgubit ćete podatke koje niste pohranili na trenutnom zahtjevu. Jeste li sigurni da želite odustati od ispunjavanja zahtjeva?"
      />
    </>
  );
}
